import React, { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    MultiPageVideoModel,
    PreviewTypes,
    SpecificationExporter,
    VideoWebSpecificationParser,
} from '@bynder-studio/render-core';
import { creativeModelFactory } from '@bynder-studio/render-web';
import { catchResponseError } from 'packages/helpers/helpers';
import { dataURItoArrayBuffer } from 'packages/helpers/helpers';
import { prepareDataForShotsCalculate } from 'packages/common/editor/editor.bloc';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import debounce from 'packages/helpers/debounce';
import { getUserFonts } from 'packages/store/user/user.actions';
import defaultFontFile from '../../../../src/assets/fonts/SourceSansPro-Regular.ttf';
import shotsCalculateService from 'packages/services/shotsCalculate';
import { flattenTree } from 'packages/common/editor/helpers/elementtree';
import { getShotsFromPageAccordingDuration } from 'packages/helpers/template';
import { shouldRecalculateShotsOnElementUpdate } from 'packages/pages/design/Design/utils';
import { normalizeVariationTemplateDesRenderOrders } from '../sidebar/variations/utils';

const defaultFontBlob = dataURItoArrayBuffer(defaultFontFile);

export const DesignContext = createContext({});

export function DesignProvider({
    children,
    template: rawTemplate,
    fonts,
    textStyles,
    contentProperties,
    creativeType,
    creativeId,
    creativeVersionId,
    creativeName,
    isMultiVersionCreative,
    downloadModalRef,
    uploadModalRef,
}) {
    const dispatch = useDispatch();
    const forceUpdate = useForceUpdate();

    // todo: the template is mutated in specification parser (the order of pages),
    // so we create the normalized version of it with correct render orders and pass it everywhere
    // when we pass it only to the factory, the original template might have a different page order from the creative model
    const template = useMemo(() => normalizeVariationTemplateDesRenderOrders(rawTemplate), [rawTemplate]);

    const data = useRef({
        loading: true,
        isUpdatedByVariation: false,
        creativeModel: undefined,
        assetLoader: undefined,
        creativeModelFactory: undefined,
        validationManager: undefined,
        canvasRenderer: undefined,
        baseDuration: 0,
        updatedPlaybackDuration: 0,
        creativeType,
        template,
        creativeId,
        creativeVersionId,
        creativeName,
        fonts,
        textStyles,
        isMultiVersionCreative,
    });
    const openDownloadModal = useCallback(() => {
        downloadModalRef.current?.open();
    }, [downloadModalRef]);

    const openUploadModal = useCallback(() => {
        uploadModalRef.current?.open();
    }, [uploadModalRef]);

    const setUpdatedByVariation = useCallback(() => {
        data.current.isUpdatedByVariation = true;
        forceUpdate();
    }, [forceUpdate]);

    // const getFirstVariationData = useCallback(
    //     () =>
    //         new Promise((resolve, reject) =>
    //             variationSetsService.fetchVariationSetsByAggregate(creativeVersionId, {
    //                 groupBy: 'variation',
    //                 limit: 1,
    //                 includeProperties: true,
    //                 includeHasDeletedAssetFlag: true,
    //                 orderBy: 'updated',
    //                 sortingOrder: 'DESC',
    //                 page: 0,
    //             })
    //                 .then(({ json: { items = [] } }) => resolve(items))
    //                 .catch(reject),
    //         ),
    //     [creativeVersionId, fonts, template],
    // );

    const initCreativeModel = useCallback(async () => {
        // const templateToUse = await updateTemplateByVariation();

        creativeModelFactory(creativeType, {
            containerDocument: window.document,
            template,
            defaultFontBlob,
            fontFamilies: fonts,
            textStyles,
            contentProperties,
            previewType: PreviewTypes.CONTENT,
        }).then((modelFactory) => {
            const creativeModel = modelFactory.getCreativeModel();
            data.current.creativeModelFactory = modelFactory;
            data.current.creativeModel = creativeModel;
            data.current.assetLoader = modelFactory.getAssetLoader();
            data.current.validationManager = modelFactory.getValidationManager();
            data.current.loading = false;
            data.current.validationManager.validate();
            data.current.baseDuration =
                creativeType === 'VIDEO' ? creativeModel.getPlaybackDuration()?.getDuration() : 0;
            data.current.updatedPlaybackDuration = template.pages[creativeModel.getCurrentPageIndex()].duration;

            forceUpdate();
        });
    }, [creativeType, fonts, template, textStyles, contentProperties]);

    useLayoutEffect(() => {
        initCreativeModel();

        return () => {
            if (!data.current) {
                return;
            }

            data.current.validationManager?.unsubscribe();
        };
    }, [forceUpdate]);

    const recalculateShots = useCallback(
        (newDurations = []) => {
            const { creativeModel } = data.current;
            const { height, width } = creativeModel.getDimensions();
            const selectedPageIndex = creativeModel.getCurrentPageIndex();
            const model = creativeModel.models[selectedPageIndex];

            const newDurationMap = {};
            newDurations.forEach(({ elementId, duration }) => {
                newDurationMap[elementId] = duration;
            });

            const updateDurations = (elements) => {
                Object.values(elements).forEach((element) => {
                    if (element.id in newDurationMap) {
                        element.properties.duration = newDurationMap[element.id];
                    }

                    if (element.children) {
                        updateDurations(element.children);
                    }
                });
            };

            // todo, improve after export implementation
            const rawElements = SpecificationExporter.exportElements(model.getElements());

            updateDurations(rawElements);
            const { elements } = prepareDataForShotsCalculate(false, rawElements);

            return new Promise((resolve) => {
                shotsCalculateService
                    .calculate({
                        height,
                        width,
                        duration: data.current.baseDuration,
                        creativeId,
                        doDynamicLengthAdjustments: true,
                        elements,
                        pageId: Number(creativeModel.getCurrentPageId()),
                    })
                    .then(({ json, status }) => {
                        if (status === 200) {
                            data.current.updatedPlaybackDuration = json.duration;
                            model.updatePlaybackDuration(json.duration);
                            forceUpdate();

                            const { templateElements } = json;
                            const timelineElements = Object.values(flattenTree(templateElements))
                                .filter((element) =>
                                    ['IMAGE', 'VIDEO', 'TEXT', 'AUDIO', 'SHAPE', 'GROUP'].includes(element.type),
                                )
                                .map((el) => ({
                                    id: el.id,
                                    type: el.type,
                                    name: el.name,
                                    startFrame: el.properties.startFrame,
                                    duration: el.properties.duration,
                                    renderOrder: el.properties.renderOrder,
                                }));

                            resolve(newDurations.length ? timelineElements : []);

                            Promise.resolve().then(() => {
                                setTimeout(() => {
                                    // cutting elements duration that is longer than the page duration
                                    // and recalculate shot duration
                                    // this should be done in the backend, but we have what we have
                                    json.shots = getShotsFromPageAccordingDuration({
                                        ...json,
                                        elements: json.templateElements,
                                    });

                                    const shots = VideoWebSpecificationParser.parseShots(json);
                                    creativeModel.replaceShots(shots);
                                    forceUpdate();
                                }, 0);
                            });
                        } else {
                            resolve([]);
                        }
                    })
                    .catch(catchResponseError);
            });
        },
        [creativeId, forceUpdate],
    );

    const initCanvasRenderer = useCallback(
        (canvasWrapper, currentFrame = 0) => {
            const canvasRenderer =
                data.current.creativeModelFactory.getCreativePreviewManipulationRenderer(canvasWrapper);

            canvasRenderer.init().then(async () => {
                if (creativeType === 'VIDEO') {
                    await canvasRenderer.setCurrentFrame(currentFrame);
                }
                data.current.canvasRenderer = canvasRenderer;
                forceUpdate();
            });
        },
        [forceUpdate],
    );

    const fontLoadErrorRetry = useCallback(() => {
        dispatch(getUserFonts());
    }, []);

    useEffect(() => {
        const { creativeModel, canvasRenderer, assetLoader } = data.current;
        if (!creativeModel || !canvasRenderer || !assetLoader) {
            return;
        }

        const handleCurrentSizeChange = () => {
            if (canvasRenderer) {
                canvasRenderer.calculateScaleRatio();

                // canvasRenderer.redraw();
                const compModel = canvasRenderer.creativeModel.getCompModel(canvasRenderer.frameIndex);
                canvasRenderer.drawCompModel(compModel);

                canvasRenderer?.pausePlayback?.();

                if (creativeModel instanceof MultiPageVideoModel) {
                    const pageDuration =
                        creativeModel.getPlaybackDuration().getDuration() ||
                        template.pages[creativeModel.getCurrentPageIndex()].duration;

                    // In case active page duration is less than current frame index
                    if (canvasRenderer.frameIndex > pageDuration) {
                        canvasRenderer.frameIndex = 0;
                    }
                }
            }
        };

        creativeModel.getEventEmitter().on('currentPageChange', handleCurrentSizeChange);
        assetLoader.eventEmitter.on('font.load.error', fontLoadErrorRetry);

        return () => {
            creativeModel.getEventEmitter().off('currentPageChange', handleCurrentSizeChange);
            canvasRenderer?.pausePlayback?.();
            assetLoader.eventEmitter.off('font.load.error', fontLoadErrorRetry);
        };
    }, [data.current.canvasRenderer, data.current.creativeModel, forceUpdate]);

    useEffect(() => {
        const creativeModel = data.current.creativeModel;

        if (!creativeModel) {
            return;
        }

        const eventEmitter = creativeModel.getEventEmitter();
        const handleModelUpdate = () => {
            forceUpdate();
        };

        eventEmitter.on('elementUpdated', handleModelUpdate);
        eventEmitter.on('currentPageChange', handleModelUpdate);

        return () => {
            eventEmitter.off('elementUpdated', handleModelUpdate);
            eventEmitter.off('currentPageChange', handleModelUpdate);
        };
    }, [data.current.creativeModel, forceUpdate]);

    useEffect(() => {
        const creativeModel = data.current.creativeModel;

        if (!creativeModel || creativeType !== 'VIDEO') {
            return;
        }

        let shouldRunRecalculation = false;
        const eventEmitter = creativeModel.getEventEmitter();
        const recalculateRelevantShots = debounce(() => {
            if (!shouldRunRecalculation) {
                return;
            }

            shouldRunRecalculation = false;
            recalculateShots();
        }, 10);
        const handleRelevantElementUpdate = (elementUpdatePayload) => {
            if (!shouldRecalculateShotsOnElementUpdate(elementUpdatePayload)) {
                return;
            }

            shouldRunRecalculation = true;
            recalculateRelevantShots();
        };

        eventEmitter.on('elementUpdated', handleRelevantElementUpdate);

        return () => {
            eventEmitter.off('elementUpdated', handleRelevantElementUpdate);
        };
    }, [creativeType, data.current.creativeModel, recalculateShots]);

    const setCurrentPageIndex = useCallback(
        (idx) => {
            data.current?.creativeModel.setCurrentPageIndex(idx);
            forceUpdate();
        },
        [forceUpdate],
    );

    const setPausePlayback = useCallback(() => {
        if (creativeType === 'VIDEO') {
            data.current.canvasRenderer?.pausePlayback?.();
        }
    }, [creativeType]);

    const setPlayPlayback = useCallback(() => {
        if (creativeType === 'VIDEO') {
            setTimeout(() => {
                data.current.canvasRenderer?.startPlayback?.();
            }, 100);
        }
    }, [creativeType]);

    const isPlaying = useCallback(() => {
        if (creativeType === 'VIDEO') {
            return data.current.canvasRenderer?.getIsPlaying();
        }

        return false;
    }, [creativeType]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }

            data.current?.canvasRenderer?.redraw();
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [isPlaying, data.current.canvasRenderer]);

    useEffect(() => {
        const setFontFamilies = data.current?.creativeModelFactory?.setFontFamilies;

        if (setFontFamilies && fonts.length) {
            setFontFamilies(fonts);
        }
    }, [fonts]);

    if (data.current.loading) {
        return null;
    }

    const dimension = data.current.creativeModel?.getDimensions();
    const width = dimension?.getWidth();
    const height = dimension?.getHeight();

    const value = {
        ...data.current,
        width,
        height,
        initCanvasRenderer,
        openDownloadModal,
        openUploadModal,
        recalculateShots,
        setCurrentPageIndex,
        setUpdatedByVariation,
        setPausePlayback,
        setPlayPlayback,
        isPlaying,
    };

    return <DesignContext.Provider value={value}>{children}</DesignContext.Provider>;
}
